import { useRef, useState } from "react";
import { ChevronDown12Regular, ChevronRight12Regular } from "@fluentui/react-icons";
import type { AnalysisOrder } from "../../redux/analysisOrder";
import type { AnalysisResultData } from "../../../../services/AnalysisResultService";
import type { FileReference } from "../../OiReport";
import { normalizeSamples } from "../utils";
import { useStickySummaryTop } from "../useStickySummaryTop";
import { GRADIENT_UNIT, isPreparationSkipped } from "../resultOrderSelection";
import { getSectionPresentation, SectionStatusPillProps } from "./shared";

type NormalizedSample = ReturnType<typeof normalizeSamples>[number];

interface OrderSummaryAsideProps {
  order: AnalysisOrder;
  orderId: string;
  normalizedSamples: NormalizedSample[];
  resultData: AnalysisResultData;
  onOpenFileGallery?: (files: FileReference[], initialIndex: number) => void;
}

function SummaryStatusBadge({ label, tone }: SectionStatusPillProps) {
  const color = tone === "done"
    ? "border-good-green text-good-green"
    : tone === "neutral"
      ? "border-oi-button-blue text-primary-dark dark:text-primary-light"
      : "border-oi-button-blue dark:border-oi-powder-form-yellow text-oi-button-blue dark:text-oi-powder-form-yellow";

  return <span className={`inline-flex h-6 w-16 shrink-0 items-center justify-center rounded-[4px] border text-[10px] leading-none ${color}`}>{label}</span>;
}

function OrderInformation({ order, orderId }: Pick<OrderSummaryAsideProps, "order" | "orderId">) {
  const fields = [
    ["Order ID", orderId],
    ["Linked powder order", order.linkedPowderOrderLabel || order.linkedPowderOrder],
    ["Requested by", order.requestedBy || order.author],
    ["Requester department", order.requesterDepartment],
    ["Order date", order.orderDate],
    ["Requested delivery date", order.requestedDeliveryDate],
    ["Priority", order.priority],
    ["Reason for higher priority", order.reasonForHigherPriority],
    ["Return material?", order.returnMaterial ? "Yes" : "No"],
  ];

  return (
    <section aria-label="Order information">
      <h3 className="mb-2 text-[12px] font-bold text-primary-dark dark:text-primary-light">Order information</h3>
      <dl className="grid gap-y-[8.5px]">
        {fields.map(([label, value]) => (
          <div key={label} className="grid grid-cols-[158px,minmax(0,1fr)] gap-x-3">
            <dt className="whitespace-nowrap">{label}</dt>
            <dd className="min-w-0 truncate" title={value || "-"}>{value || "-"}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Order files are listed under Order information so they can be opened from the summary. */
export function AttachedFiles({ files, onOpenFileGallery }: { files: FileReference[]; onOpenFileGallery?: (files: FileReference[], initialIndex: number) => void }) {
  if (files.length === 0) return null;
  return (
    <section aria-label="Attached files" className="mt-4">
      <h3 className="mb-2 text-[12px] font-bold text-primary-dark dark:text-primary-light">Attached files</h3>
      <ul className="grid gap-1">
        {files.map((file, index) => (
          <li key={file.uuid} className="min-w-0">
            <button type="button" className="max-w-full truncate text-left text-oi-button-blue underline dark:text-blue-300" title={file.fileName} onClick={() => onOpenFileGallery?.(files, index)}>{file.fileName}</button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function formatRowLabel(rowLabel: string) {
  return rowLabel === "Gradient" ? `${rowLabel} (${GRADIENT_UNIT})` : rowLabel;
}

export function OrderSummaryAside({ order, orderId, normalizedSamples, resultData, onOpenFileGallery }: OrderSummaryAsideProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({ hardness: true });
  const asideRef = useRef<HTMLElement>(null);

  useStickySummaryTop(asideRef);

  const toggleSection = (analysisKey: string) => {
    setExpandedSections(previous => ({ ...previous, [analysisKey]: !previous[analysisKey] }));
  };

  return (
    <aside ref={asideRef} aria-label="Order summary" className="mt-8 min-h-[876px] min-w-0 lg:sticky lg:top-[var(--order-summary-top,0px)] lg:max-h-[calc(100vh-var(--order-summary-top,0px)-0.75rem)] lg:min-h-[min(876px,calc(100vh-var(--order-summary-top,0px)-0.75rem))] lg:overflow-y-auto bg-white dark:bg-oi-dark-background pb-6 pl-[17px] pr-[6px] pt-3 text-[12.5px] leading-[18px] text-primary-dark/90 dark:text-primary-light/90">
      <h2 className="mb-[26px] text-[14px] font-bold leading-5 text-primary-dark dark:text-primary-light">Order summary</h2>
      <OrderInformation order={order} orderId={orderId} />
      <AttachedFiles files={order.uploadedFiles ?? []} onOpenFileGallery={onOpenFileGallery} />

      <section aria-label="Samples" className="mt-5 border-t border-primary-dark/35 dark:border-primary-light/35 pb-[19px] pt-4">
        <h3 className="mb-2 text-[12px] font-bold text-primary-dark dark:text-primary-light">Samples ({normalizedSamples.length})</h3>
        <div className="space-y-[11px]">
          {normalizedSamples.map(sample => (
            <div key={sample.sampleId} className="grid grid-cols-[158px,minmax(0,1fr)] gap-x-3">
              <span className="truncate" title={sample.sampleId}>{sample.sampleId}</span>
              <span>Co % {sample.coPercentage?.toString().replace(".", ",") ?? "-"}</span>
            </div>
          ))}
        </div>
      </section>

      <section aria-label="Result summary" className="border-t border-primary-dark/35 dark:border-primary-light/35 pr-[5px] pt-[18px]">
        <h3 className="mb-[11px] text-[12px] font-bold text-primary-dark dark:text-primary-light">Result summary</h3>
        <div className="space-y-[11px]">
          {isPreparationSkipped(order) && (
            <div className="flex min-h-6 w-full items-center justify-between gap-2">
              <span className="pl-3">Mechanical preparation</span>
              <span className="inline-flex h-[15px] items-center justify-center rounded-full border border-oi-button-blue px-1.5 text-[10px] leading-none text-primary-dark dark:text-primary-light">No preparation required</span>
            </div>
          )}
          {resultData.sections.map(section => (
            <div key={section.analysisKey}>
              <button
                type="button"
                className="flex min-h-6 w-full items-center justify-between gap-2 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-oi-button-blue"
                aria-expanded={!!expandedSections[section.analysisKey]}
                onClick={() => toggleSection(section.analysisKey)}
              >
                <span className="flex min-w-0 items-center gap-0.5">
                  {expandedSections[section.analysisKey] ? <ChevronDown12Regular className="shrink-0" /> : <ChevronRight12Regular className="shrink-0" />}
                  <span>{section.title}</span>
                </span>
                <SummaryStatusBadge {...getSectionPresentation(section)} />
              </button>
              {expandedSections[section.analysisKey] && (
                <div className="mb-[14px] mt-[10px] flex flex-wrap gap-1 pl-[11px]">
                  {section.rows.map(row => <span key={row.rowLabel} className="inline-flex h-[15px] min-w-[42px] items-center justify-center rounded-full border border-oi-button-blue px-1.5 text-[10px] leading-none text-primary-dark dark:text-primary-light">{formatRowLabel(row.rowLabel)}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </aside>
  );
}
